import { RemovableRef, useSessionStorage } from '@vueuse/core';
import { defineStore } from 'pinia';

import api from '@/api/api';
import { EXISTS_OUTCOME_URL } from '@/constants/urls';
import { IDENTIFICATION_OUTCOME } from '@/types/enums';
import { ExistsOutcomeResponse, OtpMetadata } from '@/types/responseTypes';
import { getRequestError, isDateOver30DaysAgo } from '@/utils/helperFunctions';

import useUserStore from './user';

interface State {
  userExists: boolean | null
  validationOutcome: IDENTIFICATION_OUTCOME | null
  lastReconfirmedDetailsDateTime: string | null,
  skipReconfirmation: RemovableRef<boolean>,
  otpMetadata?: OtpMetadata | null
}

export default defineStore('outcome', {
  state: (): State => ({
    userExists: null,
    validationOutcome: null,
    lastReconfirmedDetailsDateTime: null,
    otpMetadata: null,
    skipReconfirmation: useSessionStorage('skipReconfirmation', false),
  }),
  getters: {
    isPositive(state) {
      return !!state.userExists && state.validationOutcome === IDENTIFICATION_OUTCOME.POSITIVE;
    },
    isKbaVerificationRequired(state) {
      return (
        state.validationOutcome === IDENTIFICATION_OUTCOME.KBA_VERIFICATION_REQUIRED ||
        state.validationOutcome === IDENTIFICATION_OUTCOME.OTP_OR_KBA_VERIFICATION_REQUIRED
      );
    },
    isKbaVerificationFailed(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.KBA_VERIFICATION_FAILED;
    },
    isOtpVerificationRequired(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.OTP_VERIFICATION_REQUIRED;
    },
    isOtpRequested(): boolean {
      return this.isOtpVerificationRequired && useUserStore()?.otpInfo.sendAttemptsLeft !== null;
    },
    isNegativeOtp(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.NEGATIVE_OTP;
    },
    isNegative(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.NEGATIVE;
    },
    isLocked(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.LOCKED;
    },
    isAlreadyRegistered(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.ALREADY_REGISTERED;
    },
    isThinFile(state) {
      return state.validationOutcome === IDENTIFICATION_OUTCOME.THIN_FILE;
    },
    isReconfirmationRequired(state): boolean {
      if (state.skipReconfirmation) return false;

      const { lastReconfirmedDetailsDateTime } = state;
      const isOver30Days =
        lastReconfirmedDetailsDateTime && isDateOver30DaysAgo(lastReconfirmedDetailsDateTime);
      return this.isPositive && (isOver30Days || !lastReconfirmedDetailsDateTime);
    },
  },
  actions: {
    clearSession() {
      this.skipReconfirmation = false;
    },
    async getExistsOutcome(): Promise<void> {
      try {
        const res = await api<ExistsOutcomeResponse>({
          url: EXISTS_OUTCOME_URL,
          method: 'get',
          skipLogging: true,
        });

        this.userExists = res.data.userExists;
        this.validationOutcome = res.data.validationOutcome ?? null;
        this.lastReconfirmedDetailsDateTime = res.data.lastReconfirmedDetailsDateTime ?? null;
        this.otpMetadata = res.data.otpMetadata ?? null;
        window.$log.info(`[EXISTS OUTCOME]: request successful`, { data: res.data });
      } catch (e) {
        const { logMessage, errorData } = getRequestError({ url: EXISTS_OUTCOME_URL, error: e });
        if (errorData?.status !== 401 && errorData?.status !== 403) {
          window.$log.error(`[EXISTS OUTCOME]: ${logMessage}`, { error: errorData });
        }
        this.$reset();
      }
    },
  },
});
